import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Link } from "react-router-dom"
import { ForgotPasswordFormSchema, type ForgotPasswordForm } from "@/lib/schemas"
import { supabase } from "@/lib/supabase"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { BrandLockup } from "@/components/BrandLockup"

export function ForgotPasswordPage() {
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const form = useForm<ForgotPasswordForm>({
    resolver: zodResolver(ForgotPasswordFormSchema),
    defaultValues: { email: "" },
  })

  const {
    register,
    formState: { errors },
  } = form

  const onSubmit = async (data: ForgotPasswordForm) => {
    setIsLoading(true)
    setError(null)
    try {
      const appUrl = window.location.origin
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${appUrl}/reset-password`,
      })
      if (resetError) throw resetError
      setSubmitted(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center hero-gradient p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-6">
          <BrandLockup iconSize={24} wordmarkClassName="text-2xl" />
        </div>
        <Card className="p-4">
        <CardHeader className="space-y-1">
          {submitted ? (
            <>
              <div className="flex justify-center">
                <CheckCircle2 className="h-8 w-8 text-[hsl(var(--status-success))]" />
              </div>
              <CardTitle className="text-lg text-center">Check your email</CardTitle>
              <CardDescription className="text-center">
                We've sent a password reset link to your email address. It may take a minute to arrive.
              </CardDescription>
            </>
          ) : (
            <>
              <CardTitle className="text-lg text-center">Reset your password</CardTitle>
              <CardDescription className="text-center">
                Enter your email and we'll send you a reset link.
              </CardDescription>
            </>
          )}
        </CardHeader>

        {submitted ? (
          <CardFooter className="flex flex-col space-y-4">
            <Link to="/login" className="w-full">
              <Button variant="ghost" className="w-full">
                Back to login
              </Button>
            </Link>
          </CardFooter>
        ) : (
          <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
            <CardContent className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  autoFocus
                  {...register("email")}
                  className={cn(errors.email && "border-destructive")}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send Reset Link
              </Button>
              <Link
                to="/login"
                className="text-sm text-[hsl(var(--brand))] hover:text-[hsl(var(--brand-hover))] hover:underline text-center"
              >
                Back to login
              </Link>
            </CardFooter>
          </form>
        )}
      </Card>
      </div>
    </div>
  )
}
